var _a;
import { useEffect, useState } from "react";
import { cleanup, getActiveStoreContainer } from "./store";
// @ts-ignore
(_a = Symbol.dispose) !== null && _a !== void 0 ? _a : (Symbol.dispose = Symbol("Symbol.dispose"));
export const signalDebugHooks = {};
export class ObserverContext {
    static get current() {
        return ObserverContext.stack[ObserverContext.stack.length - 1];
    }
    constructor(type) {
        this.type = type;
        this._getters = new Set();
        this._setters = new Set();
        this.stack = "";
        ObserverContext.stack.push(this);
        if (signalDebugHooks.onGetValue) {
            this.stack = new Error().stack || "";
        }
        // Use for memory leak debugging
        // registry.register(this, this.id + " has been collected");
    }
    registerGetter(signal) {
        // We do not allow having getters when setting a signal, the reason is to ensure
        // no infinite loops
        if (this._setters.has(signal)) {
            return;
        }
        this._getters.add(signal);
    }
    registerSetter(signal) {
        // We do not allow having getters when setting a signal, the reason is to ensure
        // no infinite loops
        if (this._getters.has(signal)) {
            this._getters.delete(signal);
        }
        this._setters.add(signal);
    }
    /**
     * There is only a single subscriber to any ObserverContext
     */
    subscribe(onUpdate) {
        this._onUpdate = onUpdate;
        this._getters.forEach((signal) => {
            signal.addContext(this);
        });
        return () => {
            this._getters.forEach((signal) => {
                signal.removeContext(this);
            });
        };
    }
    notify() {
        var _a;
        (_a = this._onUpdate) === null || _a === void 0 ? void 0 : _a.call(this, ObserverContext.version++);
    }
    [Symbol.dispose]() {
        ObserverContext.stack.pop();
    }
}
ObserverContext.version = 0;
ObserverContext.stack = [];
export class SignalTracker {
    constructor(getValue) {
        this.getValue = getValue;
        this.contexts = new Set();
    }
    addContext(context) {
        this.contexts.add(context);
    }
    removeContext(context) {
        this.contexts.delete(context);
    }
    notify() {
        // A context can be synchronously added back to this signal related to firing the signal, which
        // could cause a loop. We only want to notify the current contexts
        const contexts = Array.from(this.contexts);
        contexts.forEach((context) => context.notify());
    }
}
// We resolving contexts with an active ObserverContext, where we do not
// want to track any signals accessed
function isResolvingContextFromComponent(context) {
    return context.type === "component" && getActiveStoreContainer();
}
export function signal(initialValue) {
    let currentAbortController;
    let value = initialValue && initialValue instanceof Promise
        ? createPromise(initialValue)
        : initialValue;
    const signal = new SignalTracker(() => value);
    function createPromise(promise) {
        currentAbortController === null || currentAbortController === void 0 ? void 0 : currentAbortController.abort();
        const abortController = (currentAbortController = new AbortController());
        return createPendingPromise(promise
            .then(function (resolvedValue) {
            if (abortController.signal.aborted) {
                return;
            }
            value = createFulfilledPromise(Promise.resolve(resolvedValue), resolvedValue);
            signal.notify();
            return resolvedValue;
        })
            .catch((rejectedReason) => {
            if (abortController.signal.aborted) {
                return;
            }
            const rejectedPromise = Promise.reject(rejectedReason);
            value = createRejectedPromise(rejectedPromise, rejectedReason);
            signal.notify();
            return rejectedPromise;
        }));
    }
    return {
        get value() {
            if (ObserverContext.current &&
                !isResolvingContextFromComponent(ObserverContext.current)) {
                ObserverContext.current.registerGetter(signal);
                if (signalDebugHooks.onGetValue) {
                    signalDebugHooks.onGetValue(ObserverContext.current, signal);
                }
            }
            return value;
        },
        set value(newValue) {
            var _a;
            if (value === newValue) {
                if (process.env.NODE_ENV === "development" &&
                    typeof newValue === "object" &&
                    newValue !== null) {
                    console.warn("You are setting the same object in a signal, which will not trigger observers. Did you mutate it?", newValue);
                }
                return;
            }
            if (newValue instanceof Promise) {
                newValue = createPromise(newValue);
            }
            const prevValue = value;
            value = newValue;
            if (signalDebugHooks.onSetValue) {
                signalDebugHooks.onSetValue(signal, value);
                (_a = ObserverContext.current) === null || _a === void 0 ? void 0 : _a.registerSetter(signal);
            }
            if (value === prevValue) {
                return;
            }
            if (value instanceof Promise) {
                // We might set a Promise.resolve, in which case we do not want to notify as the micro task has
                // already done it in "createPromise". So we run our own micro task to check if the promise
                // is still pending, where we do want to notify
                Promise.resolve().then(() => {
                    if (value instanceof Promise && value.status === "pending") {
                        signal.notify();
                    }
                });
            }
            else {
                signal.notify();
            }
        },
    };
}
function createPendingPromise(promise) {
    return Object.assign(promise, {
        status: "pending",
    });
}
function createFulfilledPromise(promise, value) {
    return Object.assign(promise, {
        status: "fulfilled",
        value,
    });
}
function createRejectedPromise(promise, reason) {
    return Object.assign(promise, {
        status: "rejected",
        reason,
    });
}
export function use(promise) {
    if (promise.status === "pending") {
        throw promise;
    }
    if (promise.status === "rejected") {
        throw promise.reason;
    }
    return promise.value;
}
export function derived(cb) {
    let value;
    let disposer;
    let isDirty = true;
    const signal = new SignalTracker(() => value);
    return {
        get value() {
            if (ObserverContext.current &&
                !isResolvingContextFromComponent(ObserverContext.current)) {
                ObserverContext.current.registerGetter(signal);
                if (signalDebugHooks.onGetValue) {
                    signalDebugHooks.onGetValue(ObserverContext.current, signal);
                }
            }
            if (isDirty) {
                disposer === null || disposer === void 0 ? void 0 : disposer();
                const context = new ObserverContext("derived");
                value = cb();
                // @ts-ignore
                context[Symbol.dispose]();
                disposer = context.subscribe(() => {
                    isDirty = true;
                    signal.notify();
                });
                isDirty = false;
                if (signalDebugHooks.onSetValue) {
                    signalDebugHooks.onSetValue(signal, value, true);
                }
            }
            return value;
        },
    };
}
export function effect(cb) {
    let currentSubscriptionDisposer;
    const updater = () => {
        const context = new ObserverContext("effect");
        cb();
        context[Symbol.dispose]();
        if (signalDebugHooks.onEffectRun) {
            signalDebugHooks.onEffectRun(cb);
        }
        return context.subscribe(() => {
            currentSubscriptionDisposer();
            currentSubscriptionDisposer = updater();
        });
    };
    currentSubscriptionDisposer = updater();
    return cleanup(currentSubscriptionDisposer);
}
export function observer() {
    const context = new ObserverContext("component");
    const [_, setState] = useState();
    useEffect(() => context.subscribe(setState), [context]);
    return context;
}
//# sourceMappingURL=signal.js.map